import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { defaultLocale } from '@/constants/locales'
import { useExperimentContext } from '@/experimentation'
import { GuildAccessNotch } from '@/molecules/GuildAccessNotch'
import { WatchHoverModal } from '@/organisms/WatchHoverModal'
import { LinkViewModel } from '@/services/RenderService'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import useWindowSize from '@/utils/useWindowSize'

interface NumberedPosterLinkProps {
  link: LinkViewModel
  className?: string
  isGuildMember?: boolean
  rank?: number
}

export function NumberedPosterLink(props: NumberedPosterLinkProps) {
  const track = useSafeTrack()
  const [isHovered, setIsHovered] = useState(false)
  const [canShowHover, setCanShowHover] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const { innerWidth } = useWindowSize()
  const { getFeatureValue } = useExperimentContext()
  const { locale } = useLocale()
  const { isGuildMember = false, link, rank } = props
  const isDoubleDigits = String(rank).length === 2

  const handleClick = React.useCallback(() => {
    if (link.track) track(link.track?.eventName, link.track?.payload)
  }, [link.track, track])

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)

  useEffect(() => {
    if (innerWidth > 1023 && locale === defaultLocale) {
      const canShowHoverModal = getFeatureValue('watch-hover-modal-desktop', false)
      if (canShowHoverModal) {
        setCanShowHover(true)
      }
    }
  }, [innerWidth, getFeatureValue, locale])

  if (!link.imageUrl) return null

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={classNames(
        'relative flex items-center space-x-4 py-4 mr-4', // keeps number + poster together
        'pl-[30px] md:pl-[50px] lg:pl-[65px] 2xl:pl-[85px]',
      )}
    >
      {/* Rank Number */}
      {typeof rank === 'number' && (
        <span
          className={classNames(
            'absolute top-5 text-[80px] font-extrabold leading-none tracking-tighter md:text-[120px] lg:text-[140px] 2xl:text-[170px] left-0',
            {
              'text-[70px] md:text-[105px] lg:text-[119px] 2xl:text-[150px]': isDoubleDigits,
            },
          )}
          style={{
            background: 'linear-gradient(to bottom, #512D12, #905F37)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            WebkitTextStroke: '1px #A87245', // thinner border
          }}
        >
          {rank}
        </span>
      )}

      {/* Poster card */}
      <div className="relative z-40 rounded-2xl shadow-[-16px_4px_25px_0_#000]">
        <InternalLink
          href={link.linkUrl}
          aria-label={`project-tile-${link.name}`}
          className="relative block h-fit max-h-full overflow-hidden rounded-2xl duration-300 ease-in-out hover:z-10 md:hover:scale-[1.05]"
        >
          <Image
            aria-label={link.alt}
            alt={link.alt}
            src={link.imageUrl}
            height={490}
            width={294}
            className="rounded-2xl bg-black"
          />
          <GuildAccessNotch isInEarlyAccess={link.isInEarlyAccess} isGuildMember={isGuildMember} />
        </InternalLink>
      </div>

      {/* Hover modal */}
      {canShowHover && <WatchHoverModal guid={link?.id} isVisible={isHovered} projectSlug={link?.projectSlug} />}
    </div>
  )
}
